import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";
import i18n from "@/i18n";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ApiErrorResponse {
  error: string;
  message: string;
  details?: unknown;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api";
const TOKEN_KEY = "katalog-access-token";
const REFRESH_TOKEN_KEY = "katalog-refresh-token";

// ─── Token Helpers ───────────────────────────────────────────────────────────

export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

// ─── Axios Instance ──────────────────────────────────────────────────────────

const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ─── Request Interceptor ─────────────────────────────────────────────────────

// Track whether a token refresh is already in progress so we don't fire
// multiple concurrent refresh requests.
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
}

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Attach Bearer token if available
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Attach current i18n language as Accept-Language header
    const currentLang = i18n.language || "fr";
    if (config.headers) {
      config.headers["Accept-Language"] = currentLang;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response Interceptor ────────────────────────────────────────────────────

apiClient.interceptors.response.use(
  // Successful responses pass through unchanged.
  (response) => response,

  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // ── Handle 401 — attempt automatic token refresh ────────────────────
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // If the failing request was itself the refresh call, bail out to
      // avoid an infinite loop.
      if (originalRequest.url?.includes("/auth/refresh")) {
        clearTokens();
        redirectToLogin();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Another refresh is already in-flight; queue this request and
        // resolve/reject it once the refresh completes.
        return new Promise<string | null>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        clearTokens();
        isRefreshing = false;
        redirectToLogin();
        return Promise.reject(error);
      }

      try {
        const { data } = await axios.post(`${BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        const newAccessToken: string = data.access_token;
        const newRefreshToken: string = data.refresh_token;

        setTokens(newAccessToken, newRefreshToken);
        processQueue(null, newAccessToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // ── Handle network errors ───────────────────────────────────────────
    if (!error.response) {
      console.error("[API] Network error:", error.message);
      // The caller can inspect `error.message` to show a toast, e.g.
      // "Unable to reach the server."
    }

    // ── Handle 403 ──────────────────────────────────────────────────────
    if (error.response?.status === 403) {
      console.warn("[API] Forbidden:", error.response.data?.message);
    }

    // ── Handle 429 ──────────────────────────────────────────────────────
    if (error.response?.status === 429) {
      console.warn("[API] Rate limited. Retry after:", error.response.headers["retry-after"]);
    }

    // ── Handle 500+ ─────────────────────────────────────────────────────
    if (error.response && error.response.status >= 500) {
      console.error("[API] Server error:", error.response.data?.message);
    }

    return Promise.reject(error);
  },
);

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Redirect the user to the login page.
 *
 * We check the current URL to extract the language prefix so the user
 * stays in the same language context.
 */
function redirectToLogin(): void {
  const lang = i18n.language || "fr";
  const loginPath = `/${lang}/login`;

  // Only redirect if we're not already on the login page
  if (!window.location.pathname.includes("/login")) {
    window.location.href = loginPath;
  }
}

/**
 * Extract a user-friendly error message from an Axios error.
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosErr = error as AxiosError<ApiErrorResponse>;

    // Server returned a structured error body
    if (axiosErr.response?.data?.message) {
      return axiosErr.response.data.message;
    }

    // Standard HTTP status messages
    switch (axiosErr.response?.status) {
      case 400:
        return "Bad request. Please check your input.";
      case 401:
        return "Your session has expired. Please log in again.";
      case 403:
        return "You do not have permission to perform this action.";
      case 404:
        return "The requested resource was not found.";
      case 409:
        return "A conflict occurred. The resource may already exist.";
      case 422:
        return "The provided data could not be processed.";
      case 429:
        return "Too many requests. Please wait a moment and try again.";
      default:
        if (axiosErr.response && axiosErr.response.status >= 500) {
          return "A server error occurred. Please try again later.";
        }
    }

    // Network error (no response at all)
    if (!axiosErr.response) {
      return "Unable to connect to the server. Please check your internet connection.";
    }
  }

  // Fallback for non-Axios errors
  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred.";
}

export default apiClient;
